/**
 * Upload Session Storage Utility
 *
 * Manages persistent storage of multipart upload sessions for videos.
 * Follows the simple key-based pattern used elsewhere (e.g. auth storage),
 * keyed by contentId so we can easily resume for a given movie/content.
 */

export interface UploadSession {
  contentId: string;
  key: string;
  uploadId: string;
  partCount: number;
  filename: string;
  fileSize: number;
  chunkSize: number;
  quality: string;
  videoType: string;
  makeDefault: boolean;
  contentType: string;
  createdAt: number; // timestamp (ms)
}

export interface EpisodeUploadSession extends UploadSession {
  dramaId: string;
  episodeId: string;
}

const STORAGE_PREFIX = 'upload_session_';
const EPISODE_STORAGE_PREFIX = 'episode_upload_session_';

function getStorageKey(contentId: string): string {
  return `${STORAGE_PREFIX}${contentId}`;
}

function getEpisodeStorageKey(dramaId: string, episodeId: string): string {
  return `${EPISODE_STORAGE_PREFIX}${dramaId}_${episodeId}`;
}

/**
 * Save upload session to localStorage.
 * Non-critical: failures are logged but not thrown.
 */
export function saveUploadSession(session: UploadSession): void {
  try {
    const key = getStorageKey(session.contentId);
    const payload = JSON.stringify(session);
    localStorage.setItem(key, payload);
  } catch (error) {
    // Storage failures should not break the upload flow
    // eslint-disable-next-line no-console
    console.error('Failed to save upload session:', error);
  }
}

/**
 * Retrieve upload session for a given contentId from localStorage.
 */
export function getUploadSession(contentId: string): UploadSession | null {
  try {
    const key = getStorageKey(contentId);
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as UploadSession;
    if (!parsed || parsed.contentId !== contentId) {
      return null;
    }

    return parsed;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to read upload session:', error);
    return null;
  }
}

/**
 * Remove upload session for a given contentId.
 */
export function clearUploadSession(contentId: string): void {
  try {
    const key = getStorageKey(contentId);
    localStorage.removeItem(key);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to clear upload session:', error);
  }
}

/**
 * Save upload session for an episode (scoped by dramaId + episodeId).
 * Non-critical: failures are logged but not thrown.
 */
export function saveEpisodeUploadSession(session: EpisodeUploadSession): void {
  try {
    const key = getEpisodeStorageKey(session.dramaId, session.episodeId);
    const payload = JSON.stringify(session);
    localStorage.setItem(key, payload);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save episode upload session:', error);
  }
}

/**
 * Retrieve upload session for a given episode (dramaId + episodeId).
 */
export function getEpisodeUploadSession(
  dramaId: string,
  episodeId: string
): EpisodeUploadSession | null {
  try {
    const key = getEpisodeStorageKey(dramaId, episodeId);
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as EpisodeUploadSession;
    if (!parsed || parsed.dramaId !== dramaId || parsed.episodeId !== episodeId) {
      return null;
    }

    return parsed;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to read episode upload session:', error);
    return null;
  }
}

/**
 * Remove upload session for a given episode (dramaId + episodeId).
 */
export function clearEpisodeUploadSession(dramaId: string, episodeId: string): void {
  try {
    const key = getEpisodeStorageKey(dramaId, episodeId);
    localStorage.removeItem(key);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to clear episode upload session:', error);
  }
}

